import { useCallback, useEffect, useRef, useState } from 'react';
import { GetTargetScrollTop, useStickToBottom } from 'use-stick-to-bottom';

/** Gap kept between the top of the scroll area and a pinned turn. */
export const PINNED_TURN_OFFSET_PX = 16;

/** How far content may extend below the fold before "Jump to latest" shows. */
export const CONTENT_BELOW_THRESHOLD_PX = 80;

/** Marks the element wrapping each conversation turn (a prompt and its reply). */
export const CHAT_TURN_ATTRIBUTE = 'data-chat-turn';

/**
 * Where the conversation rests: the top of the latest turn, so a sent prompt
 * sits at the top of the panel and its reply grows below it instead of
 * dragging the prompt out of view. use-stick-to-bottom clamps the result to
 * the real bottom, so a short conversation still rests at the bottom.
 */
export const getLatestTurnScrollTop: GetTargetScrollTop = (
  bottomScrollTop,
  { scrollElement, contentElement },
) => {
  const turns = contentElement.querySelectorAll(`[${CHAT_TURN_ATTRIBUTE}]`);
  const latestTurn = turns[turns.length - 1];
  if (!latestTurn) {
    return bottomScrollTop;
  }
  const turnTop =
    latestTurn.getBoundingClientRect().top -
    scrollElement.getBoundingClientRect().top +
    scrollElement.scrollTop;
  return turnTop - PINNED_TURN_OFFSET_PX;
};

export const hasContentBelow = ({
  scrollHeight,
  clientHeight,
  scrollTop,
}: Pick<HTMLElement, 'scrollHeight' | 'clientHeight' | 'scrollTop'>) =>
  scrollHeight - clientHeight - scrollTop > CONTENT_BELOW_THRESHOLD_PX;

/**
 * Scroll behavior for the conversation: rests on the latest turn (see
 * {@link getLatestTurnScrollTop}) rather than chasing a streaming reply past
 * the fold, and reports when there is content below so the panel can offer a
 * jump to it. Taking that jump follows the reply to its end from then on,
 * until the user scrolls away or a new turn starts.
 */
export const useChatScroll = ({
  isTurnRunning,
}: {
  /** Whether a prompt is being answered; each new turn is pinned. */
  isTurnRunning: boolean;
}) => {
  const isFollowingEndRef = useRef(false);
  // use-stick-to-bottom keeps the first function it is given, so this stays
  // stable and reads the mode from the ref.
  const getTargetScrollTop = useCallback<GetTargetScrollTop>(
    (bottomScrollTop, elements) =>
      isFollowingEndRef.current
        ? bottomScrollTop
        : getLatestTurnScrollTop(bottomScrollTop, elements),
    [],
  );
  const {
    scrollRef: stickScrollRef,
    contentRef: stickContentRef,
    scrollToBottom,
  } = useStickToBottom({
    resize: 'instant',
    initial: 'instant',
    targetScrollTop: getTargetScrollTop,
  });
  const [isContentBelow, setIsContentBelow] = useState(false);
  const scrollElementRef = useRef<HTMLElement | null>(null);
  const contentElementRef = useRef<HTMLElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const updateIsContentBelow = useCallback(() => {
    const scrollElement = scrollElementRef.current;
    setIsContentBelow(scrollElement ? hasContentBelow(scrollElement) : false);
  }, []);

  // Content can end up below the fold without a scroll: the reply grows, or
  // the scroll area shrinks (a taller composer, a shorter window).
  const watchSize = useCallback(
    (previous: HTMLElement | null, next: HTMLElement | null) => {
      if (!resizeObserverRef.current) {
        resizeObserverRef.current = new ResizeObserver(updateIsContentBelow);
      }
      if (previous) {
        resizeObserverRef.current.unobserve(previous);
      }
      if (next) {
        resizeObserverRef.current.observe(next);
      }
    },
    [updateIsContentBelow],
  );

  const scrollRef = useCallback(
    (element: HTMLElement | null) => {
      scrollElementRef.current?.removeEventListener(
        'scroll',
        updateIsContentBelow,
      );
      watchSize(scrollElementRef.current, element);
      scrollElementRef.current = element;
      element?.addEventListener('scroll', updateIsContentBelow, {
        passive: true,
      });
      stickScrollRef(element);
      updateIsContentBelow();
    },
    [stickScrollRef, updateIsContentBelow, watchSize],
  );

  const contentRef = useCallback(
    (element: HTMLElement | null) => {
      watchSize(contentElementRef.current, element);
      contentElementRef.current = element;
      stickContentRef(element);
    },
    [stickContentRef, watchSize],
  );

  // Every new turn -- sent from the composer, a suggestion or Retry -- comes
  // to the top of the panel, even if the user had scrolled up to read earlier
  // answers. History replacing the conversation doesn't start a turn, so it
  // leaves the view where it is.
  const wasTurnRunningRef = useRef(isTurnRunning);
  useEffect(() => {
    if (isTurnRunning && !wasTurnRunningRef.current) {
      isFollowingEndRef.current = false;
      scrollToBottom('smooth');
    }
    wasTurnRunningRef.current = isTurnRunning;
  }, [isTurnRunning, scrollToBottom]);

  /** Scrolls to the very end of the conversation and keeps up with a reply
   * that is still streaming. */
  const scrollToEnd = useCallback(() => {
    isFollowingEndRef.current = true;
    scrollToBottom('smooth');
  }, [scrollToBottom]);

  return {
    scrollRef,
    contentRef,
    scrollToEnd,
    isContentBelow,
  };
};
